import { toTitleCase, normalizePersonName, normalizePhoneNumber, normalizeBirthday, isValidEmail, isValidPhoneNumber } from './stringUtils';
import { TEAM_ROLE_KEYS, getRoleStyle } from './roleStyles';
import { toDateOnly } from './dateOnly';

// ---- Canonical schema for members CSV ----
export const CANONICAL_ORDER = ['name', 'email', 'phoneNumber', 'role', 'allergies', 'birthday'];

export const MEMBER_ROLES_OPTIONS = TEAM_ROLE_KEYS.map((roleKey) => ({
  value: roleKey,
  label: getRoleStyle(roleKey).label,
}));

export const ROLE_CONFIG = TEAM_ROLE_KEYS.reduce((acc, roleKey) => {
  const { bg, text, label } = getRoleStyle(roleKey);
  acc[roleKey] = { bg, text, label };
  return acc;
}, {});

export const HEADER_ALIASES = {
  'name': 'name',
	'Name': 'name',
	'email': 'email',
	'Email': 'email',
	'Phone Number': 'phoneNumber',
	'phone #': 'phoneNumber',
	'Phone #': 'phoneNumber',
	'phone number': 'phoneNumber',
	'phone_number': 'phoneNumber',
	'phone': 'phoneNumber',
	'role': 'role',
	'Role': 'role',
	'allergies': 'allergies',
	'Allergies': 'allergies',
	'birthday': 'birthday',
	'Birthday': 'birthday',
	'bday': 'birthday',
	'Bday': 'birthday',
};

export const prettyLabelFor = (k) => ({
  name: 'Name',
  email: 'Email',
  phoneNumber: 'Phone Number',
  role: 'Role',
  allergies: 'Allergies',
  birthday: 'Birthday',
}[k] || k);

// ---- Helpers ----
export const slug = (s) =>
  String(s || '')
    .trim()
    .toLowerCase()
    .replace(/[\s/]+/g, '-')
    .replace(/[^a-z0-9-_]/g, '');

export const splitCsvLine = (line) => {
  const out = [];
  let cur = '', inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { cur += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      out.push(cur.trim()); cur = '';
    } else cur += ch;
  }
  out.push(cur.trim());
  return out;
};

const normalizeHeader = (h) => HEADER_ALIASES[String(h || '').trim().toLowerCase()] || String(h || '').trim().toLowerCase();

export const normalizeMember = (m = {}) => {
  const roleLower = String(m.role || 'player').toLowerCase();
  return {
    name: normalizePersonName(m.name),
    email: String(m.email || '').trim().toLowerCase(),
    phoneNumber: normalizePhoneNumber(m.phoneNumber || ''),
    role: MEMBER_ROLE_KEYS.includes(roleLower) ? roleLower : 'player',
    allergies: toTitleCase(m.allergies || ''),
    birthday: normalizeBirthday(m.birthday || ''), // -> yyyy-MM-dd or ''
  };
};

const MEMBER_ROLE_KEYS = ['player', 'coach', 'staff'];

// What stops a roster row from being saved, in the coach's words. The import
// preview and the add-member rows are edited by hand, so the rows are checked
// again right before they are written — a bad one used to reach the database,
// which refused the whole batch.
export const memberRowProblems = (m = {}) => {
  const problems = [];
  if (!String(m.name || '').trim()) problems.push('needs a name');
  const email = String(m.email || '').trim();
  if (!email) problems.push('needs an email');
  else if (!isValidEmail(email)) problems.push(`"${email}" is not a valid email`);
  // A phone is optional, but "123" is not one: it was saved as is, and SMS
  // could then be turned on for a number nobody can text (QA 2026-10-06).
  // Setup and CSV rows carry phoneNumber, the Add Member rows phone.
  const phone = String(m.phoneNumber || m.phone || '').trim();
  if (phone && !isValidPhoneNumber(phone)) problems.push(`"${phone}" is not a valid phone number`);
  const birthday = String(m.birthday || '').trim();
  if (birthday && normalizeBirthday(birthday) !== birthday) problems.push(`"${birthday}" is not a valid birthday`);
  return problems;
};

// One line per bad row ("Row 3 (Ben Blocker): ..."); [] when every row can be saved.
// existingEmails: the team's members now. The import preview and the add rows
// are edited by hand, so a row can pick up a current member's email after the
// file was read; the database refuses that row and the whole batch with it.
export const validateMemberRows = (rows = [], { existingEmails = [] } = {}) => {
  const onTeam = new Set(
    [...(existingEmails || [])].map((email) => String(email || '').trim().toLowerCase()).filter(Boolean)
  );
  return (Array.isArray(rows) ? rows : []).flatMap((row, index) => {
    const problems = memberRowProblems(row);
    const email = String(row?.email || '').trim().toLowerCase();
    if (email && onTeam.has(email)) problems.push(`${email} is already on the team`);
    if (!problems.length) return [];
    const who = String(row?.name || '').trim();
    return [`Row ${index + 1}${who ? ` (${who})` : ''}: ${problems.join(', ')}.`];
  });
};

// The team_members row for a member added by hand: the Team page's Add
// member form and the assistant's roster card both insert this. A phone's SMS
// consent fields are the caller's to add (the Team page's auto opt-in).
export const buildNewMemberRow = ({ teamId, name, email, role, phone = '', allergies = '', birthday = null }) => {
  const normalizedPhone = normalizePhoneNumber(phone || '');
  return {
    team_id: teamId,
    user_id: null,
    role: String(role || 'player').toLowerCase(),
    full_name: normalizePersonName(name),
    email: String(email || '').toLowerCase(),
    phone_number: normalizedPhone && normalizedPhone.trim() ? normalizedPhone : null,
    allergies: toTitleCase(allergies || ''),
    birthday: birthday || null,
    // no is_active
  };
};

// --- Dedupe helpers (email-based) ---

// Build a stable key (email only; change here if you later want phone fallback)
export const dedupKeyFor = (m) => {
  const email = String(m?.email || '').trim().toLowerCase();
  return email ? `e:${email}` : null;
};

// Finds duplicates *within* a list. Returns groups like:
// [ [{idx, m}, {idx, m}], [{idx, m}, {idx, m}, {idx, m}] ]
export const findIntraListDuplicates = (arr, keyFn = dedupKeyFor) => {
  const map = new Map();
  const groups = [];
  arr.forEach((m, idx) => {
    const key = keyFn(m);
    if (!key) return;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push({ idx, m });
  });
  for (const [, list] of map) {
    if (list.length > 1) groups.push(list);
  }
  return groups;
};

// Pretty message for banners: one line per email that appears more than once.
// It used to read "Please remove 1. groups" for two pairs, and "remove 1" for
// three of the same email when two had to go.
export const duplicatesMessage = (groups) => {
  if (!groups?.length) return '';
  const lines = groups.map(g => `• ${g.map(({ m }) => m.email).join(', ')}`);
  return `You have duplicate emails. Keep one entry for each email below and remove the rest:\n${lines.join('\n')}`;
};

// ---- Parse CSV -> { rows, errors[], warnings[] } ----
// existingLabel: where the rows already on screen live, in the note about the
// ones skipped. Team setup has no team yet, so "already on your team" was
// wrong there.
export const parseMembersCsv = (
  csvText,
  { existingMembers = [], currentUser = null, existingLabel = 'already on your team' } = {}
) => {
  const errors = [];
  const warnings = [];

  try {
    let text = String(csvText || '').replace(/^\uFEFF/, ''); // strip BOM
    const lines = text.split(/\r\n|\n|\r/).filter(l => l.trim() !== '');
    if (lines.length <= 1) {
      errors.push('CSV file is empty or contains only headers.');
      return { rows: [], errors, warnings };
    }

    // headers
    const headerMeta = splitCsvLine(lines[0])
      .map((raw, idx) => ({ raw: String(raw || '').trim(), canon: normalizeHeader(raw), idx }))
      .filter(h => h.raw.length > 0 && h.canon.length > 0);

    const headers = headerMeta.map(h => h.canon);

    // required
    const required = ['name', 'email'];
    const missing = required.filter(k => !headers.includes(k));
    if (missing.length) {
      errors.push(`CSV is missing required headers: ${missing.map(prettyLabelFor).join(', ')}.`);
      return { rows: [], errors, warnings };
    }

    // unknown headers
    const allowed = new Set(CANONICAL_ORDER);
    const unknownOriginal = headerMeta.filter(h => !allowed.has(h.canon)).map(h => h.raw);
    if (unknownOriginal.length) {
      warnings.push(
        `Unrecognized header${unknownOriginal.length > 1 ? 's' : ''}: ${unknownOriginal.join(', ')}. ` +
        `Allowed headers are: ${CANONICAL_ORDER.map(prettyLabelFor).join(', ')}.`
      );
    }

    // normalize existing emails (for dedupe against team)
    const existingEmailsLower = new Set(
      (existingMembers || [])
        .map(x => {
          // support shapes: {email}, {m: {email}}, raw string, etc.
          if (typeof x === 'string') return x.toLowerCase();
          if (x?.email) return String(x.email).toLowerCase();
          if (x?.m?.email) return String(x.m.email).toLowerCase();
          return '';
        })
        .filter(Boolean)
    );

    const rows = [];
    const csvSeen = new Set();
    const alreadyOnTeam = new Set();
    const csvDupes = new Set();
    // Everything the import changes or drops is said out loud: a coach who
    // uploads 20 people and sees 18 has to know which two, and why.
    const incompleteLines = [];
    const invalidEmails = [];
    const unknownRoles = [];
    const invalidBirthdays = [];

    lines.slice(1).forEach((line, lineIndex) => {
      const values = splitCsvLine(line);
      if (values.every(v => v === '')) return;

      const raw = {};
      headerMeta.forEach(({ canon, idx }) => { raw[canon] = values[idx] ?? ''; });

      const member = normalizeMember(raw);

      if (!member.name || !member.email) {
        // +2: the header is line 1, and people count from 1.
        incompleteLines.push(lineIndex + 2);
        return;
      }

      const rawRole = String(raw.role || '').trim();
      if (rawRole && !MEMBER_ROLE_KEYS.includes(rawRole.toLowerCase())) {
        unknownRoles.push(`${member.name} ("${rawRole}")`);
      }
      const rawBirthday = String(raw.birthday || '').trim();
      if (rawBirthday && !member.birthday) {
        invalidBirthdays.push(`${member.name} ("${rawBirthday}")`);
      }
      // Kept in the preview, where the typo can be fixed; the import itself
      // refuses to run while one is left (validateMemberRows).
      if (!isValidEmail(member.email)) {
        invalidEmails.push(`${member.name} ("${member.email}")`);
      }

      const emailLower = member.email.toLowerCase();

      if (existingEmailsLower.has(emailLower)) {
        alreadyOnTeam.add(emailLower);
        return;
      }

      if (csvSeen.has(emailLower)) {
        csvDupes.add(emailLower);
        return;
      }

      csvSeen.add(emailLower);
      rows.push(member);
    });

    if (currentUser?.email) {
      const idx = rows.findIndex(r => r.email.toLowerCase() === currentUser.email.toLowerCase());
      if (idx !== -1) rows[idx].role = 'coach';
    }

    // A file whose every row was already there (the same file picked twice)
    // is not a broken file: the note below says what was skipped.
    if (!rows.length && !errors.length && !alreadyOnTeam.size && !csvDupes.size) {
      errors.push('CSV file does not contain any valid member data rows.');
    }

    if (incompleteLines.length) {
      warnings.push(
        `Skipped ${incompleteLines.length} row${incompleteLines.length > 1 ? 's' : ''} missing a name or an email ` +
        `(line${incompleteLines.length > 1 ? 's' : ''} ${incompleteLines.join(', ')} of the file).`
      );
    }
    if (invalidEmails.length) {
      warnings.push(
        `${invalidEmails.length} email${invalidEmails.length > 1 ? 's do' : ' does'} not look right: ` +
        `${invalidEmails.join(', ')}. Fix or remove ${invalidEmails.length > 1 ? 'them' : 'it'} before importing.`
      );
    }
    if (unknownRoles.length) {
      warnings.push(
        `Roles must be player, coach or staff. Set to Player: ${unknownRoles.join(', ')}.`
      );
    }
    if (invalidBirthdays.length) {
      warnings.push(
        `${invalidBirthdays.length} birthday${invalidBirthdays.length > 1 ? 's are' : ' is'} not a real date and ` +
        `${invalidBirthdays.length > 1 ? 'were' : 'was'} left blank: ${invalidBirthdays.join(', ')}.`
      );
    }

    if (alreadyOnTeam.size) {
      warnings.push(
        `${alreadyOnTeam.size} entr${alreadyOnTeam.size > 1 ? 'ies are' : 'y is'} ${existingLabel} ` +
        `(${[...alreadyOnTeam].join(', ')}). They were skipped.`
      );
    }
    if (csvDupes.size) {
      warnings.push(
        `Ignored ${csvDupes.size} duplicate entr${csvDupes.size > 1 ? 'ies' : 'y'} in the CSV ` +
        `(${[...csvDupes].join(', ')}).`
      );
    }

    return { rows, errors, warnings };
  } catch (e) {
    console.error('parseMembersCsv failed:', e);
    errors.push('Failed to parse CSV file. Please check the format and try again.');
    return { rows: [], errors, warnings };
  }
};

// ---- Export helpers (CSV) ----

// Convert your DB rows -> export rows. Birthday goes out as YYYY-MM-DD, which
// the importer reads back: an export then re-import used to lose every one.
export const membersToExportRows = (teamMembers = []) =>
  teamMembers.map((m) => ({
    name: m.full_name || m.name || '',
    email: m.email || '',
    role: m.role || '',
    phoneNumber: m.phone_number || m.phoneNumber || '',
    allergies: m.allergies || '',
    birthday: String(m.birthday || '').slice(0, 10),
  }));

export const EXPORT_COLUMNS = [
  { key: 'name',       label: 'Name' },
  { key: 'email',      label: 'Email' },
  { key: 'role',       label: 'Role' },
  { key: 'phoneNumber',label: 'Phone' },
  { key: 'allergies',  label: 'Allergies' },
  { key: 'birthday',   label: 'Birthday' },
];

export const rowsToCsv = ({ columns = EXPORT_COLUMNS, rows = [] }) => {
  const header = columns.map(c => c.label);
  const body = rows.map(r => columns.map(c => r[c.key] ?? ''));
  const escape = (s) => {
    const str = String(s ?? '');
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };
  return [header, ...body].map(row => row.map(escape).join(',')).join('\n');
};

export const downloadCsv = ({ rows, columns = EXPORT_COLUMNS, filename = 'export.csv' }) => {
  const csv = rowsToCsv({ columns, rows });
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

export const buildMembersFilename = (teamInfo, { selectedCount = null, totalCount = null, date = new Date(), ext = 'csv' } = {}) => {
  const d = toDateOnly(date);
  const pieces = [slug(teamInfo?.name) || 'team', slug(teamInfo?.sport), slug(teamInfo?.gender)].filter(Boolean).join('_');
  let scope = "";
  if (selectedCount != null) {
    scope = `-selected-${selectedCount}`;
  }
  return `${pieces || 'team'}-members-${d}${scope}.${ext}`;
};
